import { finalPrice, isPrice } from './finalPricing';
import { PRICE_ARMS } from './priceArm';

/**
 * The per-adult `seen_price` a result card hands to the pick (BOOK-237): the headline it painted.
 *
 * The headline already carries the card's arm, its leg pricing (round-trip, or a coupon or wallet
 * quote the list swapped in) and the winning provider discount, flat fare or passenger category, so
 * the pick reports what was seen without recomputing any of it. It returns undefined, so the pick
 * falls back through `pickSeenPrice`, for a headline in points (not an amount), INS-1288's netted
 * arm (whose seen price stays gross, KD4) and a card that paints no price.
 *
 * @param {object} card - What the card painted.
 * @param {number|string} [card.priceTotal] - The headline figure.
 * @param {string} [card.arm] - The card's arm, one of `PRICE_ARMS`.
 * @param {boolean} [card.paintsPoints] - Whether the headline is shown in wallet points.
 * @returns {number|string|undefined} The seen price to hand to the pick.
 */
export const cardSeenPrice = ({ priceTotal, arm, paintsPoints = false }) => {
  if (paintsPoints || arm === PRICE_ARMS.NETTED) return undefined;
  if (!isPrice(priceTotal) || Number(priceTotal) <= 0) return undefined;

  return priceTotal;
};

/**
 * The seen price a pick sends: the card's (`context.seenPrice`) when the pick came from a card,
 * otherwise the trip's one-way total in the pick's arm, as every pick did before BOOK-237.
 *
 * The arm may be passed as a function, so a pick that needs it only for this fallback reads
 * GrowthBook only when the card handed no seen price.
 *
 * @param {object} [context] - The selection context the card passed with the pick.
 * @param {object} trip - The picked trip, as a plain object.
 * @param {boolean|Function} [isFinal] - The arm the pick resolved (see `reportsFinalPrice`), or a
 *   function that resolves it.
 * @returns {number|string|undefined} The seen price to send.
 */
export const pickSeenPrice = (context, trip, isFinal = false) => {
  if (context?.seenPrice !== undefined) return context.seenPrice;

  return finalPrice(trip?.pricing, typeof isFinal === 'function' ? isFinal() : isFinal);
};
